/* Copyright (c) 2017 Backpack Health LLC */

import React from 'react';
import { connect } from 'react-redux';
import * as _ from 'lodash';

import SignupEmail from '../views/SignupEmail';
import SetPassword from '../views/SetPassword';

import { ActionItem, ActionItemViewName } from '../../../models/ActionItemModel';
import { actions as actionItemActions, selectors as actionItemsSelector } from '../../redux/ducks/actionItems';
import { updateEmail, actions as userActions, selectors as userSelector } from '../../redux/ducks/user';
import { actions as systemNotificationActions } from '../../redux/ducks/systemNotification';

import RouteNames from '../../appAuthorized/RouteNames';
import SessionService from '../../../apiServices/SessionService';
import AppUtils from '../../../utilities/AppUtils';
import UserService from '../../../apiServices/UserService';
import UserMetrics, { TrackEvent, UserMetricsProfile } from '../../../utilities/UserMetrics';
import { joinGroupIntent } from '../../../utilities/LocalStorage';
import { selectors as policySelectors } from '../../redux/ducks/policy';
import { UserData } from '../../../models/UserModel';
import { PolicyInterface, PolicyName } from '../../../models/PolicyModel';
import { navigateBack, navigateReplace } from '../../appAuthorized/redux/NavigationActions';

function mapStateToProps(state) {
  return {
    userActionItems: actionItemsSelector.getNewUserActionItems(state),
    isActionItemsLoaded: actionItemsSelector.isNewUserActionItemsLoaded(state),
    email: userSelector.getEmail(state),
    localeId: userSelector.getLocaleId(state),
    getPolicyByName: (name) => policySelectors.getPolicyByName(state, name),
    goToAndroidIntro: () => {
      navigateBack(RouteNames.introEnd());
    },
    goToLogin: () => {
      navigateBack(RouteNames.login());
    },
    goToNewUserSetup: () => {
      navigateReplace(RouteNames.newUserSetup());
    },
  };
}

function mapDispatchToProps(dispatch) {
  return {
    loadInitialData: () => {
      dispatch(actionItemActions.requestNewUserActionItems());
    },
    updateEmail: (email) => {
      dispatch(updateEmail(email));
    },
    showNotification: () => {
      dispatch(systemNotificationActions.showNotification());
    },
    updateUser: (userData, callback) => {
      dispatch(userActions.submitUserUpdate(userData, (apiError) => {
        if (apiError) {
          dispatch(systemNotificationActions.showNotification());
          this.props.showNotification(apiError.messageKey());
        }
        callback(apiError);
      }));
    },
    onAccept: (userId, policies, callback) => {
      UserService.provideConsent(userId, policies).then(callback);
    },
  };
}

const actionItemStepOrder = [
  {
    view: ActionItemViewName.user_signup,
    stepView: 'setEmail',
    actionItem: null,
  },
  {
    view: ActionItemViewName.user_signup,
    stepView: 'setPassword',
    actionItem: null,
  },
  {
    view: ActionItemViewName.policy_communication,
    stepView: ActionItemViewName.policy_communication,
    actionItem: null,
  },
];

interface State {
  actionItemSteps: {
    view: ActionItemViewName;
    stepView: string;
    actionItem: ActionItem;
  }[];
  actionItemIndex: number;
  stepIndex: number;
  user: UserData;
}

interface Props {
  userActionItems: ActionItem[];
  email: string;
  updateEmail(email: string): void;
  goToAndroidIntro(): void;
  localeId: number;
  showNotification(m: string): void;
  goToLogin(): void;
  updateUser(user: any, callback): void;
  getPolicyByName(name: PolicyName): PolicyInterface;
  onAccept(userId: number, policies: number[], callback): void;
  goToNewUserSetup(): void;
}

class UserSignup extends React.Component {
  props: Props;
  state: State = {
    actionItemIndex: 0,
    stepIndex: 0,
    actionItemSteps: [],
    user: null,
  };

  UNSAFE_componentWillMount() {
    const actionItemSteps = [];
    actionItemStepOrder.forEach(item => {
      const actionItem = this.props.userActionItems.filter(a => a.view === item.view)[0];
      if (actionItem) {
        actionItemSteps.push(_.assign({}, item, { actionItem }));
      }
    });

    this.setState({
      actionItemSteps,
    });
  }

  onSubmitValidPassword = (password, duration) => {
    const { email, localeId, userActionItems, showNotification, goToNewUserSetup, onAccept } = this.props;
    const groupId = joinGroupIntent.pop();
    const userSignupConsentPolicies = userActionItems.filter(a => a.view === ActionItemViewName.user_signup)[0].policy_id;

    UserService.create(
      {
        email,
        password,
        locale_id: localeId,
        intent: !groupId ? undefined : {
          type: 'join_group',
          join_group: {
            group_id: groupId,
          },
        },
      },
      (apiError, user) => {
        if (apiError) {
          showNotification(apiError.messageKey());
          return;
        }

        const { email } = user.data;

        UserMetrics.anonymousUser(user.data.cuid);
        UserMetricsProfile.init(user.data.cuid, user.data.created_at);
        TrackEvent.signup.setPassword();

        SessionService.login(
          { email, password, duration },
          (apiError) => {
            if (apiError) { return; }
            onAccept(user.data.id, userSignupConsentPolicies, () => {
              if (AppUtils.isAndroidDevice()) {
                AppUtils.reloadApp();
              } else {
                AppUtils.reloadAppToPathname(RouteNames.newUserSetup());
              }
            });
          });
      });
  }

  render() {
    const { actionItemIndex, actionItemSteps } = this.state;
    const { email, updateEmail, goToAndroidIntro, goToLogin } = this.props;

    const currentActionItem = actionItemSteps[actionItemIndex];

    switch (currentActionItem.stepView) {
      case 'setEmail':
        return (
          <SignupEmail
            email={email}
            updateEmail={updateEmail}
            goToNext={() => {
              this.setState({ actionItemIndex: actionItemIndex + 1 });
            }}
            goToLogin={goToLogin}
            goToIntro={goToAndroidIntro}
          />
        );
      case 'setPassword':
        return (
          <SetPassword
            title="setPwd.title.text"
            onBack={() => {
              this.setState({ actionItemIndex: actionItemIndex - 1 });
            }}
            onNext={this.onSubmitValidPassword}
            showStrength
            allowLanguageToggle={!IS_ANDROID_BUILD}
          />
        );
      default:
        return null;
    }
  }
}

interface UserSignupWrapperProps extends Props {
  loadInitialData(): void;
  isActionItemsLoaded: boolean;
}
class UserSignupWrapper extends React.Component {
  props: UserSignupWrapperProps;

  UNSAFE_componentWillMount() {
    this.props.loadInitialData();
  }

  render() {
    if (this.props.userActionItems.length > 0) {
      return <UserSignup {...this.props} />;
    } else {
      return null;
    }
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(UserSignupWrapper);
